<template>
  <div>
    <transition-group
      name="bar-graph"
      tag="div"
      class="bar-graph"
      :class="clusterClass"
    >
      <div
        v-for="item in sortedData"
        :key="item.label"
        class="bar"
        :class="{ first: hour === 0 }"
        :style="{ width: `${item.width}%`, backgroundColor: item.color }"
      >
        <span class="bar-label">{{ item.label }}<img
          class="bar-logo"
          :src="item.logo"
          :alt="item.label"
        ></span>
        <span class="bar-value"><img
          class="bar-logo"
          :src="item.logo"
          :alt="item.label"
        >{{ addCommas(item.votes) }}</span>
      </div>

      <div
        key="-1"
        class="timestamp"
      >
        <img
          alt="2023 Award Ceremony Logo"
          :src="logoSrc"
        >
        <div class="time">
          {{ timestamp }}
        </div>
      </div>
    </transition-group>

    <div class="labels">
      <div
        v-for="label in numberSet"
        :key="label"
        class="label"
      >
        {{ addCommas(label) }}
      </div>
    </div>
  </div>
</template>

<script>
import c1 from '@/assets/data/cluster_1_sorted.json';
import c2 from '@/assets/data/cluster_2_sorted.json';
import c3 from '@/assets/data/cluster_3_sorted.json';
import logoSrc from '@/assets/imgs/2023-awards-logo.png';

export default {

  props: {
    cluster: {
      type: Number,
      default: 1
    },

    pause: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      clusterList: [c1, c2, c3],
      innerPause: false,
      hour: 0,
      hourInterval: 1,
      timeInterval: 100,
      numSchools: 10,
      logoSrc
    };
  },

  computed: {
    data() {
      return this.clusterList[this.cluster - 1];
    },

    paused() {
      return this.pause || this.innerPause;
    },

    currentHour() {
      return this.hour;
    },

    clusterClass() {
      return `cluster-${this.cluster}`;
    },

    sortedData() {
      const set = this.data[this.hour];
      const max = this.numberSet[this.numberSet.length - 1];
      const newData = [];
      let bars = this.numSchools;

      if (this.hour === 0) {
        bars = this.data[0].length;
      }

      set.slice(0, bars);

      for (let i = 0; i < this.data[0].length; i += 1) {
        newData.push({
          votes: set[i].votes,
          width: (set[i].votes / max) * 100,
          color: set[i].color,
          label: set[i].label,
          logo: set[i].logo,
          timestamp: set[i].timestamp
        });
      }

      return newData.slice(0, bars);
    },

    highestNumber() {
      return this.getMax(this.data[this.hour]);
    },

    timestamp() {
      if (this.hour === this.data.length - 1) {
        return 'March 26 - Week 4';
      }

      const dateString = this.sortedData[0].timestamp;
      const options = {
        month: 'long',
        day: 'numeric'
      };
      const date = new Date(dateString).toLocaleDateString(undefined, options);
      const week = this.getWeek(this.data[0][0].timestamp, this.sortedData[0].timestamp);

      return `${date} - ${week}`;
    },

    numberSet() {
      const numberSet = [];
      const highestDigit = 10 ** Math.floor(Math.log10(this.highestNumber));
      const maxNumber = highestDigit * Math.ceil(this.highestNumber / highestDigit);
      let numberSetLength = Math.ceil(maxNumber / highestDigit);
      if (maxNumber % highestDigit === 0) {
        numberSetLength += 1;
      }
      for (let i = 0; i < numberSetLength; i += 1) {
        numberSet.push(i * highestDigit);
      }

      if (numberSet.length === 0) {
        return [0, 10];
      }

      return numberSet;
    },

    startTime() {
      const dateString = this.data[0][0].timestamp;
      const options = {
        month: 'long',
        day: 'numeric'
      };
      return new Date(dateString).toLocaleDateString(undefined, options);
    }
  },

  mounted() {
    setInterval(() => {
      if (!this.paused) {
        if (this.hour < this.data.length - 1) {
          this.hour += this.hourInterval;

          if (this.hour >= this.data.length - this.hourInterval - 1) {
            this.innerPause = true;
            this.hour = this.data.length - 1;
          }
        }
        else {
          this.hour = 0;
        }
      }
    }, this.timeInterval);
  },

  methods: {
    getMax(arr) {
      let max = 0;
      for (let i = 0; i < arr.length; i += 1) {
        if (arr[i].votes > max) {
          max = arr[i].votes;
        }
      }
      return max;
    },

    addCommas(num) {
      const strNum = num.toString();
      const chars = strNum.split('');
      chars.reverse();

      const commasAdded = [];
      for (let i = 0; i < chars.length; i += 1) {
        if (i % 3 === 0 && i !== 0) {
          commasAdded.push(',');
        }
        commasAdded.push(chars[i]);
      }

      const result = commasAdded.reverse().join('');

      return result;
    },

    getWeek(start, end) {
      const oneWeekInMs = 604800000; // 1 week in milliseconds
      const diffInMs = Date.parse(end) - Date.parse(start);
      const diffInWeeks = Math.floor(diffInMs / oneWeekInMs);

      if (diffInWeeks < 1) {
        return 'Week 1';
      }
      return `Week ${diffInWeeks + 1}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.bar-graph {
  // display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-direction: column;
  height: 600px;
  width: calc(100% - 280px);
  padding: 20px 80px 20px 200px;
  position: relative;
  z-index: 20;

  @media screen and (width <= 768px) {
    padding: 190px 50px 20px;
    width: calc(100% - 100px);
    height: 300px;
  }

  &.cluster-1 .bar {
    &.first {
      height: calc((100% / 14) - 10px);

      @media screen and (width <= 768px) {
        &.first {
          height: calc((100% / 14) - 5px);
        }
      }
    }
  }

  &.cluster-2 .bar {
    &.first {
      height: calc((100% / 24) - 10px);

      @media screen and (width <= 768px) {
        &.first {
          height: calc((100% / 24) - 5px);
        }
      }
    }
  }

  &.cluster-3 .bar {
    &.first {
      height: calc((100% / 20) - 10px);

      @media screen and (width <= 768px) {
        &.first {
          height: calc((100% / 20) - 5px);
        }
      }
    }
  }

  .bar {
    // height: 40px;
    height: calc((100% / 10) - 20px);
    margin: 20px 0;
    transition:
      all 0.4s,
      width 0.1s linear;

    @media screen and (width <= 768px) {
      transition: width 0.1s linear;
    }

    display: flex;
    align-items: center;
    position: relative;

    @media screen and (width <= 768px) {
      height: calc((100% / 10) - 10px);
      margin: 10px 0;

      &.first {
        height: calc((100% / 14) - 5px);
        margin: 5px 0;
      }
    }

    &.first {
      margin: 10px 0;

      .bar-label {
        font-size: 14px;
        font-weight: 500;
        width: 300px;
        left: -310px;

        @media screen and (width <= 768px) {
          font-size: 0;
        }
      }
    }

    .bar-label {
      position: absolute;
      left: -210px;
      width: 200px;
      text-align: right;
      font-size: 16px;
      font-weight: 600;

      @media screen and (width <= 768px) {
        font-size: 0;
      }

      .bar-logo {
        max-width: 50px;
        max-height: 30px;
        margin-right: 10px;
        display: none;

        @media screen and (width <= 768px) {
          display: inline-block;
        }
      }
    }

    .bar-value {
      position: absolute;
      right: -10px;
      width: 0;
      height: 100%;
      display: flex;
      align-items: center;
    }

    .bar-logo {
      max-height: 50px;
      height: 100%;
      margin-right: 10px;

      @media screen and (width <= 768px) {
        display: none;
      }
    }
  }

  .timestamp {
    position: absolute;
    bottom: 50px;
    right: 20px;
    text-align: center;
    transition: none !important;

    @media screen and (width <= 768px) {
      top: 10px;
      bottom: unset;
      left: 10px;
      font-size: 16px;
      text-align: center;
      font-weight: 600;
    }

    img {
      width: 200px;
      margin-bottom: 12px;

      @media screen and (width <= 768px) {
        width: 150px;
        margin-bottom: 0;
      }
    }

    .time {
      font-size: 24px;
      font-weight: 600;

      @media screen and (width <= 768px) {
        font-size: 16px;
        font-weight: 500;
      }
    }
  }
}

.labels {
  width: calc(100% - 280px);
  padding: 0 80px 20px 200px;
  display: flex;
  justify-content: space-between;
  position: relative;
  z-index: 10;
  margin-bottom: 20px;

  @media screen and (width <= 768px) {
    width: calc(100% - 100px);
    padding: 0 50px 20px;
  }

  .label {
    position: relative;
    width: 0;

    @media screen and (width <= 768px) {
      writing-mode: vertical-rl;
      text-orientation: sideways;
    }

    &::before {
      content: '';
      position: absolute;
      width: 1px;
      background: $thd-light-gray;
      bottom: calc(100% + 10px);
      height: 610px;

      @media screen and (width <= 768px) {
        height: 310px;
      }
    }
  }
}

/* Animations */
.bar-graph-enter,
.bar-graph-leave-to {
  opacity: 0;
  transform: translateY(30px);
  height: 0;
}

.bar-graph-move {
  z-index: 1;
  transform: translateY(0);
}

.bar-graph-leave-active {
  position: absolute;
  z-index: 2;
}

.bar-graph-leave-active > div {
  opacity: 0;
  transform: translateX(30px);
  height: 0;
}
</style>
